import { ImagePlus, PackagePlus, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { TagInput } from '@nucleus-commerce/admin-kit/forms';
import type { ChatShortcut } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { MediaPickerDialog, MediaThumb, useMedia } from '@/features/media';
import { ProductPicker } from '@/features/resource-pickers';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  emptyShortcut,
  fromShortcut,
  insertPlaceholder,
  toCreateShortcut,
  toUpdateShortcut,
} from '../api/mappers';
import { useServerMessage } from '../hooks/use-server-message';
import { useShortcutMutations } from '../hooks/use-shortcut-mutations';
import {
  MAX_SHORTCUT_ATTACHMENTS,
  MAX_SHORTCUT_PRODUCTS,
  MAX_SUGGESTED_REPLIES,
  SHORTCUT_PLACEHOLDERS,
  shortcutSchema,
  type ShortcutForm,
  type ShortcutScope,
} from '../schemas/shortcut-schema';

import { FormAlert } from './form-controls';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'key',
  'title',
  'body',
  'scope',
  'attachments',
  'productIds',
  'suggestedReplies',
]);

function AttachmentChip({
  id,
  onRemove,
  disabled,
}: {
  id: string;
  onRemove: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('chatSettings');
  const { data } = useMedia(id);
  const name = data?.alt ?? t('shortcuts.attachmentFallback', { id: id.slice(-6) });
  return (
    <li className="flex items-center gap-2 rounded-md border p-1 pr-2">
      <MediaThumb media={data ?? null} className="size-10" />
      <span className="max-w-32 truncate text-xs">{name}</span>
      {disabled ? null : (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('shortcuts.removeAttachment', { name })}
          onClick={onRemove}
        >
          <X aria-hidden />
        </Button>
      )}
    </li>
  );
}

export interface ShortcutDialogProps {
  storeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Editing this shortcut; null creates one in `scope`. */
  shortcut: ChatShortcut | null;
  scope: ShortcutScope;
  /** Store and organisation are offered for shared shortcuts; a personal one stays personal. */
  shared: boolean;
}

/**
 * Create or edit one shortcut: `/key`, title, body with placeholder chips, suggested replies,
 * media attachments and product cards. A dialog, so it does not register with the save bar.
 */
export function ShortcutDialog({
  storeId,
  open,
  onOpenChange,
  shortcut,
  scope,
  shared,
}: ShortcutDialogProps) {
  const { t } = useTranslation(['chatSettings', 'common']);
  const serverMessage = useServerMessage();
  const { create, update } = useShortcutMutations(storeId);
  const body = useRef<HTMLTextAreaElement | null>(null);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  /** Titles of products picked in this session; saved ones only have ids. */
  const [productTitles, setProductTitles] = useState<Map<string, string>>(new Map());
  const form = useForm<ShortcutForm>({
    resolver: useI18nResolver(shortcutSchema),
    defaultValues: shortcut ? fromShortcut(shortcut) : emptyShortcut(scope),
  });
  const attachments = useWatch({ control: form.control, name: 'attachments' });
  const productIds = useWatch({ control: form.control, name: 'productIds' });
  const saving = create.isPending || update.isPending;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      if (shortcut) await update.mutateAsync({ id: shortcut.id, body: toUpdateShortcut(values) });
      else await create.mutateAsync(toCreateShortcut(values));
      onOpenChange(false);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: serverMessage(error) });
    }
  });

  const insert = (name: string) => {
    const element = body.current;
    const current = form.getValues('body');
    const next = insertPlaceholder(
      current,
      name,
      element?.selectionStart ?? current.length,
      element?.selectionEnd ?? current.length,
    );
    form.setValue('body', next.body, {
      shouldDirty: true,
      shouldValidate: form.formState.isSubmitted,
    });
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(next.caret, next.caret);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {shortcut
              ? t('shortcuts.editTitle')
              : t(`shortcuts.newTitle.${shared ? 'shared' : 'personal'}`)}
          </DialogTitle>
          <DialogDescription>{t('shortcuts.dialogDescription')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            <FormAlert message={form.formState.errors.root?.server?.message} />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shortcuts.key')}</FormLabel>
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground font-mono">/</span>
                      <FormControl>
                        <Input className="font-mono" autoComplete="off" {...field} />
                      </FormControl>
                    </div>
                    <FormDescription>{t('shortcuts.keyHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shortcuts.titleLabel')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {shared && !shortcut ? (
              <FormField
                control={form.control}
                name="scope"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shortcuts.scope')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full sm:w-72">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="store">{t('shortcuts.scopes.store')}</SelectItem>
                        <SelectItem value="organization">
                          {t('shortcuts.scopes.organization')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>{t('shortcuts.scopeHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('shortcuts.body')}</FormLabel>
                  <div
                    className="flex flex-wrap gap-1.5"
                    role="group"
                    aria-label={t('shortcuts.placeholders')}
                  >
                    {SHORTCUT_PLACEHOLDERS.map((name) => (
                      <Button
                        key={name}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 font-mono text-xs"
                        aria-label={t('shortcuts.insertPlaceholder', { name })}
                        onClick={() => insert(name)}
                      >
                        {`{{${name}}}`}
                      </Button>
                    ))}
                  </div>
                  <FormControl>
                    <Textarea
                      rows={5}
                      {...field}
                      ref={(element) => {
                        field.ref(element);
                        body.current = element;
                      }}
                    />
                  </FormControl>
                  <FormDescription>{t('shortcuts.bodyHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="suggestedReplies"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('shortcuts.suggestedReplies')}</FormLabel>
                  <FormControl>
                    <TagInput
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      max={MAX_SUGGESTED_REPLIES}
                      maxLength={80}
                      placeholder={t('shortcuts.suggestedRepliesPlaceholder')}
                      removeLabel={(reply) => t('shortcuts.removeReply', { reply })}
                    />
                  </FormControl>
                  <FormDescription>{t('shortcuts.suggestedRepliesHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">{t('shortcuts.attachments')}</span>
              {attachments.length > 0 ? (
                <ul className="flex flex-wrap gap-2">
                  {attachments.map((id) => (
                    <AttachmentChip
                      key={id}
                      id={id}
                      disabled={false}
                      onRemove={() =>
                        form.setValue(
                          'attachments',
                          attachments.filter((other) => other !== id),
                          { shouldDirty: true },
                        )
                      }
                    />
                  ))}
                </ul>
              ) : null}
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={attachments.length >= MAX_SHORTCUT_ATTACHMENTS}
                  onClick={() => setMediaOpen(true)}
                >
                  <ImagePlus aria-hidden />
                  {t('shortcuts.addAttachments')}
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">{t('shortcuts.products')}</span>
              {productIds.length > 0 ? (
                <ul className="flex flex-wrap gap-2">
                  {productIds.map((id) => {
                    const name =
                      productTitles.get(id) ?? t('shortcuts.productFallback', { id: id.slice(-6) });
                    return (
                      <li key={id}>
                        <Badge variant="secondary" className="gap-1 pr-1">
                          {name}
                          <button
                            type="button"
                            className="hover:text-foreground rounded-sm"
                            aria-label={t('shortcuts.removeProduct', { name })}
                            onClick={() =>
                              form.setValue(
                                'productIds',
                                productIds.filter((other) => other !== id),
                                { shouldDirty: true },
                              )
                            }
                          >
                            <X className="size-3" aria-hidden />
                          </button>
                        </Badge>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setProductsOpen(true)}
                >
                  <PackagePlus aria-hidden />
                  {t('shortcuts.chooseProducts')}
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">{t('shortcuts.productsHint')}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
        <MediaPickerDialog
          open={mediaOpen}
          onOpenChange={setMediaOpen}
          excludeIds={new Set(attachments)}
          onPick={(media) =>
            form.setValue(
              'attachments',
              [...attachments, ...media.map((item) => item.id)].slice(0, MAX_SHORTCUT_ATTACHMENTS),
              { shouldDirty: true },
            )
          }
        />
        <ProductPicker
          open={productsOpen}
          onOpenChange={setProductsOpen}
          scope={{ kind: 'store', storeId }}
          title={t('shortcuts.chooseProducts')}
          multiple={MAX_SHORTCUT_PRODUCTS}
          initialSelectedIds={productIds}
          onConfirm={(selection) => {
            setProductTitles((previous) => {
              const next = new Map(previous);
              for (const item of selection.items) {
                next.set(item.productId, item.title ?? item.product.title);
              }
              return next;
            });
            form.setValue('productIds', selection.ids.slice(0, MAX_SHORTCUT_PRODUCTS), {
              shouldDirty: true,
            });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
